/**
 * WeeklyCalendarContainer — smart wrapper that binds the presentational
 * `<WeeklyCalendar />` surface to the appointments data layer.
 *
 * Responsibilities:
 *   - Week navigation state (Monday-anchored).
 *   - Staff visibility + filter state (session-scoped).
 *   - Data fetching via React Query (`useStaffList`, `useWeekAppointments`).
 *   - Hydration: ISO → Date conversion for calendar props.
 *   - Optimistic update mutation for drag-and-drop rescheduling.
 *   - Side sheet orchestration (details / new appointment placeholder).
 *
 * @see PETOTA_BUSINESS_UX_BLUEPRINT §B1.4
 */
import { PencilSimple, Wallet } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/Button';
import { SideSheet } from '@/shared/ui/SideSheet';
import {
  AppointmentDetailsPanel,
  getMondayOfWeek,
  useCalendarLabels,
  WeeklyCalendar,
} from '@/shared/ui/calendar';
import type {
  Appointment,
  FilterChip,
  TimeSlot,
  WeeklyCalendarProps,
} from '@/shared/ui/calendar/types';
import { useConnectStatus } from '@/features/connect';

import { AppointmentForm } from './AppointmentForm';
import type { AppointmentFormValues } from './appointment-form-schema';
import { toDateInputValue, toTimeInputValue } from './form-utils';
import {
  useServices,
  useCreateCashierOrderFromAppointment,
  useStaffList,
  useUpdateAppointment,
  useWeekAppointments,
} from './useAppointments';
import type { AppointmentRecord, StaffRecord } from './types';

const WORKING_HOURS = { start: '08:00', end: '20:00' } as const;

function hydrateAppointment(record: AppointmentRecord): Appointment {
  return {
    id: record.id,
    source: record.source,
    externalBookingId: record.externalBookingId,
    customerId: record.customerId,
    customerName: record.customerName,
    petId: record.petId,
    petName: record.petName,
    serviceName: record.serviceName,
    serviceCategory: record.serviceCategory,
    startTime: new Date(record.startTime),
    endTime: new Date(record.endTime),
    staffId: record.staffId,
    status: record.status,
    isVip: record.isVip,
    notes: record.notes,
    price: record.price,
    currency: record.currency,
  };
}

function isPetServisAuthorityLocked(appointment: Pick<Appointment, 'source' | 'externalBookingId'>): boolean {
  return appointment.source === 'marketplace' && Boolean(appointment.externalBookingId);
}

function hydrateStaff(record: StaffRecord): WeeklyCalendarProps['staff'][number] {
  return {
    id: record.id,
    fullName: record.fullName,
    avatarUrl: record.avatarUrl,
    role: record.role,
    isActive: record.isActive,
  };
}

export function WeeklyCalendarContainer(): JSX.Element {
  const { t } = useTranslation('calendar');
  const { t: tForm } = useTranslation('appointmentForm');
  const labels = useCalendarLabels();

  const [weekStart, setWeekStart] = useState<Date>(() => getMondayOfWeek(new Date()));
  const [visibleStaffIds, setVisibleStaffIds] = useState<string[] | undefined>(undefined);
  const [activeFilters, setActiveFilters] = useState<FilterChip[]>([]);

  const [detailsFor, setDetailsFor] = useState<Appointment | null>(null);
  const [createDraft, setCreateDraft] = useState<TimeSlot | null>(null);
  const [editFor, setEditFor] = useState<Appointment | null>(null);

  const isoMonday = weekStart.toISOString();
  const staffQuery = useStaffList();
  const servicesQuery = useServices();
  const appointmentsQuery = useWeekAppointments(isoMonday);
  const connectStatusQuery = useConnectStatus();
  const updateMutation = useUpdateAppointment(isoMonday);
  const cashierOrderMutation = useCreateCashierOrderFromAppointment();
  const isPetServisAuthority = connectStatusQuery.data?.syncAuthority === 'petservis';

  const staff = useMemo(
    () => (staffQuery.data ?? []).map(hydrateStaff),
    [staffQuery.data],
  );
  const appointments = useMemo(
    () => (appointmentsQuery.data ?? []).map((record) => {
      const appointment = hydrateAppointment(record);
      return {
        ...appointment,
        syncAuthorityLocked: isPetServisAuthority
          && isPetServisAuthorityLocked(appointment),
      };
    }),
    [appointmentsQuery.data, isPetServisAuthority],
  );

  const availableFilters: FilterChip[] = useMemo(
    () => [
      {
        id: 'f-grooming',
        label: labels.serviceCategoryLabels.grooming,
        type: 'service',
        value: 'grooming',
      },
      {
        id: 'f-vet',
        label: labels.serviceCategoryLabels.vet,
        type: 'service',
        value: 'vet',
      },
      {
        id: 'f-training',
        label: labels.serviceCategoryLabels.training,
        type: 'service',
        value: 'training',
      },
      {
        id: 'f-pending',
        label: labels.statusLabels.pending,
        type: 'status',
        value: 'pending',
      },
      {
        id: 'f-vip',
        label: labels.vipBadge,
        type: 'priority',
        value: 'vip',
      },
    ],
    [labels],
  );

  const error =
    staffQuery.error || appointmentsQuery.error
      ? {
          message:
            (staffQuery.error as Error | undefined)?.message ??
            (appointmentsQuery.error as Error | undefined)?.message ??
            '',
        }
      : null;

  const detailsStaffName = detailsFor
    ? staff.find(s => s.id === detailsFor.staffId)?.fullName
    : undefined;

  const services = servicesQuery.data;

  const createInitialValues = useMemo<Partial<AppointmentFormValues> | undefined>(() => {
    if (!createDraft) return undefined;
    return {
      staffId: createDraft.staffId,
      date: toDateInputValue(createDraft.date),
      startTime: createDraft.startTime,
    };
  }, [createDraft]);

  const editInitialValues = useMemo<
    (Partial<AppointmentFormValues> & { id?: string }) | undefined
  >(() => {
    if (!editFor) return undefined;
    const service = services?.find(s => s.name === editFor.serviceName);
    return {
      id: editFor.id,
      customerId: editFor.customerId,
      petId: editFor.petId,
      serviceId: service?.id ?? '',
      staffId: editFor.staffId,
      date: toDateInputValue(editFor.startTime),
      startTime: toTimeInputValue(editFor.startTime),
      endTime: toTimeInputValue(editFor.endTime),
      price: editFor.price ?? 0,
      currency: editFor.currency ?? 'TRY',
      notes: editFor.notes ?? '',
      isVip: editFor.isVip ?? false,
    };
  }, [editFor, services]);

  const handleUpdate = async (
    id: string,
    changes: Partial<Appointment>,
  ): Promise<void> => {
    const current = appointments.find((appointment) => appointment.id === id);
    if (current?.syncAuthorityLocked) {
      toast.error(labels.syncAuthorityLockedHelp);
      throw new Error('sync-authority-locked');
    }

    const serialized: Parameters<typeof updateMutation.mutateAsync>[0]['changes'] = {};
    if (changes.startTime instanceof Date) {
      serialized.startTime = changes.startTime.toISOString();
    }
    if (changes.endTime instanceof Date) {
      serialized.endTime = changes.endTime.toISOString();
    }
    if (typeof changes.staffId === 'string') serialized.staffId = changes.staffId;
    if (changes.status) serialized.status = changes.status;
    if (typeof changes.notes === 'string') serialized.notes = changes.notes;
    if (typeof changes.price === 'number') serialized.price = changes.price;
    if (typeof changes.currency === 'string') serialized.currency = changes.currency;

    try {
      await updateMutation.mutateAsync({ id, changes: serialized });
      toast.success(t('updateSuccess'));
    } catch {
      toast.error(t('updateFailed'));
      throw new Error('update-failed');
    }
  };

  const handleCreateCashierOrder = async (appointment: Appointment): Promise<void> => {
    try {
      await cashierOrderMutation.mutateAsync({ id: appointment.id });
      toast.success(t('cashierOrderSuccess'));
      setDetailsFor(null);
    } catch {
      toast.error(t('cashierOrderFailed'));
    }
  };

  return (
    <>
      <WeeklyCalendar
        weekStart={weekStart}
        staff={staff}
        appointments={appointments}
        workingHours={WORKING_HOURS}
        onCreateAppointment={slot => setCreateDraft(slot)}
        onUpdateAppointment={handleUpdate}
        onOpenDetails={appointment => setDetailsFor(appointment)}
        activeFilters={activeFilters}
        availableFilters={availableFilters}
        onFilterChange={setActiveFilters}
        visibleStaffIds={visibleStaffIds}
        onStaffVisibilityChange={setVisibleStaffIds}
        onWeekChange={setWeekStart}
        onJumpToToday={() => setWeekStart(getMondayOfWeek(new Date()))}
        labels={labels}
        isLoading={staffQuery.isLoading || appointmentsQuery.isLoading}
        error={error}
      />

      <SideSheet
        open={Boolean(detailsFor)}
        onOpenChange={open => {
          if (!open) setDetailsFor(null);
        }}
        title={labels.appointmentDetailsTitle}
        closeLabel={labels.closeSheet}
        footer={
          detailsFor ? (
            <div className="flex flex-wrap justify-end gap-3">
              <Button
                type="button"
                intent="secondary"
                leftIcon={<Wallet size={18} weight="regular" />}
                disabled={cashierOrderMutation.isPending}
                onClick={() => {
                  void handleCreateCashierOrder(detailsFor);
                }}
              >
                {t('cashierOrderAction')}
              </Button>
              <Button
                type="button"
                intent="primary"
                leftIcon={<PencilSimple size={18} weight="regular" />}
                disabled={detailsFor.syncAuthorityLocked}
                onClick={() => {
                  if (detailsFor.syncAuthorityLocked) {
                    toast.error(labels.syncAuthorityLockedHelp);
                    return;
                  }
                  setEditFor(detailsFor);
                  setDetailsFor(null);
                }}
              >
                {labels.editAction}
              </Button>
            </div>
          ) : null
        }
      >
        {detailsFor && (
          <AppointmentDetailsPanel
            appointment={detailsFor}
            staffName={detailsStaffName}
            labels={labels}
          />
        )}
      </SideSheet>

      <SideSheet
        open={Boolean(createDraft)}
        onOpenChange={open => {
          if (!open) setCreateDraft(null);
        }}
        title={tForm('titleCreate')}
        closeLabel={labels.closeSheet}
      >
        {createDraft && (
          <AppointmentForm
            isoMonday={isoMonday}
            mode="create"
            initialValues={createInitialValues}
            onSuccess={() => setCreateDraft(null)}
            onCancel={() => setCreateDraft(null)}
          />
        )}
      </SideSheet>

      <SideSheet
        open={Boolean(editFor)}
        onOpenChange={open => {
          if (!open) setEditFor(null);
        }}
        title={tForm('titleEdit')}
        closeLabel={labels.closeSheet}
      >
        {editFor && (
          <AppointmentForm
            isoMonday={isoMonday}
            mode="edit"
            initialValues={editInitialValues}
            onSuccess={() => setEditFor(null)}
            onCancel={() => setEditFor(null)}
          />
        )}
      </SideSheet>
    </>
  );
}
